export const enum AppEnv {
	PRODUCTION = 'production',
	DEVELOP = 'develop',
	LOCAL = 'local',
}

export const enum AppType {
	MAIN = 1,
	DATAWALL = 2,
	SHARED_CHAT = 3,
	SHARED_ARTIFACT = 4,
	MOBILE = 5,
	E2E = 958437, // arbitrary; not assigned by the server, only set client-side when seeding via /e2e
}

export const enum PopupState {
	INACTIVE = 0,
	OPENING = 1,
	OPEN = 2,
	CLOSING = 3,
}

export const enum PopupEvent {
	OPENING = 'opening',
	OPENED = 'opened',
	CLOSE_INTENT = 'close-intent', // can cancel closing
	CLOSING = 'closing',
	CLOSED = 'closed',
}

export const enum PopupCloseReason {
	CLOSEALL = 'closeall',
	ONLYCONTEXT = 'onlycontext',
	XCLICK = 'xclick',
	OUTSIDECLICK = 'outsideclick',
	NOATTACHTO = 'noattachto',
	NOSTACKED = 'nostacked',
	ESCKEY = 'esckey',
	CLOSEONBODYSCROLL = 'closeonbodyscroll',
}

export const enum ModelName {
	ALERT_SETTING = 'alert-setting',
	BLOCK = 'block',
	BLOCK_PREVIEW = 'block-preview',
	BOARD = 'board',
	BOARD_LOOP = 'board-loop',
	BOARD_PREVIEW = 'board-preview',
	BOARD_SHARE = 'board-share',
	CLIENT_METRIC = 'client-metric',
	CUSTOM_QUERY = 'custom-query',
	DATA_SELECT = 'data-select',
	FORECAST = 'forecast',
	GOAL = 'goal',
	GOAL_LOG = 'goal-log',
	GROUP = 'group',
	HORIZON_METRIC = 'horizon-metric',
	METRIC = 'metric',
	METRIC_ANOMALY = 'metric-anomaly',
	REMINDER = 'reminder',
	REPORT = 'report',
	REPORT_DELIVERY = 'report-delivery',
	REPORT_SCHEDULE = 'report-schedule',
	REPORT_SCHEDULE_SETTING = 'report-schedule-setting',
	REPORT_SLIDE = 'report-slide',
	REPORT_SLIDE_FRAGMENT = 'report-slide-fragment',
	SAMPLE = 'sample',
	SCORECARD = 'scorecard',
	SCRIPT = 'script',
	SOURCE = 'source',
	SPACE = 'space',
	TAG = 'tag',
	TEAM = 'team',
	TEAM_SECTION = 'team-section',
	TEAM_SECTION_ITEM = 'team-section-item',
	TEMPLATE = 'template',
	USER = 'user',
}

export { MetricType } from '@databox/types/domains/metric/metric.enums';

export const enum MetricStatus {
	ACTIVE = 'active',
	DISABLED = 'disabled',
}

export { DataSelectType } from '@databox/types/domains/data-select/data-select.enums';

export const enum ChangeIndicatorClass {
	POSITIVE = 'positive',
	NEGATIVE = 'negative',
}

export { AccountType } from '@databox/types/domains/account/account.enums';

export const enum PlanType {
	FREE = 'free',
	ANALYST = 'analyst',
	STARTER = 'starter',
	PROFESSIONAL = 'professional',
	PERFORMER = 'performer',
	GROWTH = 'growth',
	PREMIUM = 'premium',
	VIP = 'vip',
	CUSTOM = 'custom',
	AGENCY = 'agency',
	TEAM_CORE = 'team-core',
	TEAM_SCALE = 'team-scale',
}

export const enum PlanExtrasType {
	EXTENSION = 'extension',
	ADD_ON = 'add_on',
	ONE_TIME_SERVICE = 'one_time_service',
	TOP_UP = 'top_up',
}

export const enum UserRole {
	ADMIN = 1,
	USER = 2,
	EDITOR = 3,
	VIEWER = 4,
	DEMO = 5,
}

export const enum ManualSyncPopupType {
	LIMITATION = 1,
	UPSELL = 2,
	LIMIT_REACHED = 3,
}

export const enum AccountSetupFeatureType {
	SOURCES = 'data_sources',
	DATABOARDS = 'boards',
	CLIENTS = 'clients',
	REPORTS = 'reports',
	BOARD_LOOPS = 'board_loops',
	CUSTOM_METRICS = 'custom_metrics',
	DATA_CALCULATIONS = 'metric_calculations',
	SCORECARDS = 'scorecards',
	ALERTS = 'alerts',
	SNAPSHOTS = 'board_shares',
	METRIC_DIGESTS = 'metric_digests',
	GOALS = 'goals',
	WHITE_LABELING = 'white_labeling',
}

export const enum ProductIssueType {
	GENERAL_ERROR = 'general_error',
	GENERAL_TIMEOUT = 'general_timeout',
	GENERAL_WARNING = 'general_warning',
	DATA_QUERY_ERROR = 'data_query_error',
	DATA_QUERY_TIMEOUT = 'data_query_timeout',
	DATA_PREVIEW_ERROR = 'data_preview_error',
	DATA_PREVIEW_TIMEOUT = 'data_preview_timeout',
	DATA_WARNING = 'data_warning',
	DATA_LOADING_DELAY = 'data_loading_delay',
	DATASET_ERROR = 'dataset_error',
}

export const enum BlockType {
	UNI = 99, // do we still need this?
}

export const enum BlockVisualizationLegacy {
	FORECAST = -4,
	PROGRESS_RADIAL = -3,
	GOAL_CHART = -2,
	METRIC_CHART = -1,
	LINE_CHART = 4,
	BAR_CHART = 5,
	FUNNEL = 6,
	PIPELINE = 7,
	PROGRESS = 8,
	PIE = 10,
	COMBO_CHART = 12,
	HEATMAP = 15,
	GAUGE = 21,
	NOTE = 22,
	IMAGE = 23,
}

export const enum BlockVisualization {
	NUMBER = 1,
	LINE_CHART = 4,
	BAR_CHART = 5,
	FUNNEL = 6,
	PIPELINE = 7,
	PROGRESS = 8,
	TABLE = 9,
	PIE = 10,
	RADIAL_PROGRESS = 11,
	HORIZONTAL_BAR_CHART = 13,
	DATA_STORY = 14,
	HEATMAP = 15,
	BUBBLE = 17,
	SPIDER = 19,
	GAUGE = 21,
	TEXT = 22,
	IMAGE = 23,
	AI_SUMMARY = 24,
}

export const enum BlockVisualizationSpecial {
	METRIC_CHART = -1,
	FORECAST_CHART = -2,
	FORECAST_CHART_SMALL = -3,
	GOAL_CHART = -4,
}

// https://github.com/databox/framework/blob/master/Z/DB/Map/Block/BlockDiscriminator.php
export const enum BlockDiscriminator {
	BOARD = 0,
	SCORECARD = 2,
	MY_METRIC = 3,
	ALERT = 4,
	BOARD_PREVIEW = 7,
	REPORT_SLIDE_FRAGMENT = 8,
	TOP_ITEM = 11,
	PERFORMANCE_ITEM = 13,
	FORECAST = 14,
	METRIC_ANOMALY = 15,
	TEAMS = 16,
	GOAL = 17,
	GOAL_STATUS_CHANGE = 18,
	CLIENT_METRIC = 19,
	EXPLORER = 20,
}

export const enum SeriesVisualization {
	LINE = 'line',
	BAR = 'bar',
}

export const enum Aggregation {
	SUM = 'SUM',
	AVG = 'AVG',
	MIN = 'MIN',
	MAX = 'MAX',
	MEDIAN = 'MEDIAN',
}

export const enum SortFieldType {
	VALUE = 'value',
	CHANGE = 'change',
	NAME = 'name',
	CUSTOM = 'custom',
}

export enum ReportLayout {
	SLIDES = 0,
	PAGELESS = 1,
}

export const enum BoardCustomContainer {
	REPORT_SLIDE = 'report_slide',
	REPORT_PAGELESS = 'report_pageless',
}

export const enum FakeModelIdPrefix {
	METRIC_INSIGHTS = '1251987', // 1(I)2(N)5(S)1(I)9(G)8(H)7(t)
	FORECASTS = '40982157', // 4(f)0(o)9(r)8(e)2(c)1(a)5(s)7(t)
	METRIC_BUILDER = '80120891', // 8(b)0(u)1(i)2(l)0(d)8(e)9(r)1(!)
	DATA_CALCULATION = '81122112', // 8(d)1(a)1(t)2(a)2(c)1(a)1(l)2(c)
	GOAL_INSIGHTS = '9012125', // 9(g)0(o)1(a)2(l)1(i)2(n)5(s)
	OTHER = '67832', // 6(o)7(t)8(h)3(e)2(r)
}

export const enum BoardPopupShareTab {
	USER_ACCESS = 'user-access',
	LINK = 'shareable-link',
	SEND = 'send',
	EMBED = 'embed',
	TV = 'stream-to-tv',
}

export const enum NewReportSlideLayout {
	COVER = 1,
	BOARD = 2,
	COLUMNS_1 = 3,
	COLUMNS_2 = 4,
	COLUMNS_3 = 5,
	DATA_STORY = 6,
}

export const enum NewReportSlideFragmentLayout {
	TITLE = 'title',
	TITLE_2 = 'title_2',
	TEXT = 'text',
	IMAGE = 'image',
	METRIC = 'metric',
	DATABOARD = 'databoard',
	DATA_STORY = 'data_story',
	AI_PERFORMANCE_SUMMARY = 'ai_performance_summary',
	TABLE = 'table',
	DIVIDER = 'divider',
}

export const enum MetricLibraryGridType {
	BASIC = 'basic',
	CUSTOM = 'custom',
}

export const Unit = {
	ALL: '-1',
	EMPTY: '-0',
	// only for serializers
	ALL_BE: null,
	EMPTY_BE: '',
};

export const Scale = {
	AUTO: -1,
	AUTO_BE: null,
};

export const enum BlockRenderStep {
	TOP = 'top', // date picker
	TITLE = 'title', // block title
	PRE_NUMBERS = 'pre_numbers', // prepares the multi-number grid layout
	SINGLE_NUMBER = 'single_number', // autoscales for seperate numbers
	POST_NUMBERS = 'post_numbers', // scaling for the whole number group + slider for multi-number
	CHART = 'chart', // highchart blocks (content::chart)
	CONTENT = 'content', // other content (table block, text block, ..)
}

export const enum RcItem {
	OTHER = 0,
	CONNECT_SOURCE = 1,
	CHOOSE_SOURCE = 2,
	BOARD_SHARE = 3,
	INACTIVITY_WARNING = 4,
	// BOARD_LOOP_SHARE = 5, // currently unused
	// REQUEST_CONNECT = 6, // unused since AMA ph 3
	REPORT_SHARE = 7,
}

export const enum SnapshotFormat {
	JPG = 0,
	PDF = 1,
}

export const enum DesignerLayout {
	MOBILE = 0,
	DESKTOP = 7,
}

export const enum UserSelectionType {
	ME = 1,
	ME_AND_SELECTED = 2,
}

export { TimeUnit } from '@databox/types/domains/date-range/date-range.enums';

export const enum CompareType {
	// NONE = 0,
	RANGE = 1,
	OTHER_METRIC = 3,
	GOAL = 4,
	// below: used only in fake compare point logic
	FORECAST = 5,
	FORECAST_OPTIMISTIC = 6,
	FORECAST_PESSIMISTIC = 7,
	ANOMALY = 9,
}

export const enum CompareFunction {
	// must match with constants.mkChangeFns
	PERCENTAGE = 1,
	RATIO = 2, // default for goals
	ABSOLUTE = 3,
}

export const enum UserAccessGroup {
	ALL_ADMINS = 1,
	ADMINS_AND_SELECTED = 2,
	ALL_USERS = 3,
}

export const enum UserAccess {
	PUBLIC = 1,
	PRIVATE = 2,
	RESTRICTED = 3,
}

export const enum TableAggOperationTitle {
	SUM = 'TOTAL',
	AVG = 'AVERAGE',
	MIN = 'MINIMUM',
	MAX = 'MAXIMUM',
}

export const enum SourceTypeCategory {
	CLOUD = 1,
	SPREADSHEET = 2,
	DATABASE = 3,
	WORKFLOW = 4,
	REST_API = 5,
	PARTNER = 6,
	METRIC_BUILDER = 7,
}

export const enum ScheduleType {
	ONE_OFF = 0,
	DAILY = 1,
	WEEKLY = 2,
	MONTHLY = 3,
	QUARTERLY = 4,
	// YEARLY = 5,
	// HOURLY = 6,
}

export const enum DayOfTheWeek {
	SUNDAY = 0,
	MONDAY = 1,
	TUESDAY = 2,
	WEDNESDAY = 3,
	THURSDAY = 4,
	FRIDAY = 5,
	SATURDAY = 6,
}

export const enum DeliveryType {
	PUSH = 0,
	EMAIL = 1,
	SLACK = 2,
}

export const enum RequestException {
	USER_ALREADY_EXISTS = 100,
}

export const enum RequestFormType {
	SOURCE = 'source',
	METRICS = 'metrics',
}

export const enum TwoFAMethod {
	SMS = 1,
	APP = 2,
}

export const enum BillingCycle {
	MONTHLY = 'monthly',
	QUARTERLY = 'quarterly',
	YEARLY = 'yearly',
}

export const enum BillingFeature {
	BOARDS = 'boards',
	SOURCES = 'sources',
	USERS = 'users',
	CUSTOM_METRICS = 'custom_metrics',
	STORAGE = 'storage',
	QB = 'qb', // on all (nonlegacy) plans
	SCRIPTS = 'scripts', // on all (nonlegacy) plans
	HISTORICAL_DATA = 'historical_data_retention',
	DATA_REFRESH_INTERVAL = 'data_refresh_interval',
	SSO = 'sso',
	CUSTOM_RANGE = 'custom_range',
	ADVANCED_REPORTS = 'reports_advanced_slides',
	PREMIUM_SOURCES = 'premium_sources',
	API_METRIC_BUILDER = 'custom_api_integrations',
	FORECASTS = 'forecasting',
	FORECAST_MODELING = 'forecast_modeling',
	ALERTS = 'alerts', // del Sharing & Notifications
	GOALS = 'goals',
	GOALS_ADVANCED = 'goals_advanced',
	GOALS_FORECASTS = 'goals_forecasts',
	REPORTS_BASIC = 'reports_basic',
	DATA_PUSH = 'data_push',
	INGESTION_API = 'ingestion_api',
	WORKFLOW_INTEGRATIONS = 'workflow_integrations',
	ACCOUNT_TEMPLATES = 'account_templates',
	GENERATIVE_AI = 'generative_ai', // on all (nonlegacy) plans
	VISUALIZATIONS_ADVANCED = 'visualizations_advanced',
	SSO_FORCED = 'sso_forced',
	MFA_FORCED = 'mfa_forced',
	MFA = 'mfa',
	LOGIN_RECORDS = 'login_records',
	ACTIVE_SESSIONS = 'active_sessions',
	MEDIA_CREATIVES = 'media_creatives', // on all (nonlegacy) plans
	ANOMALY_DETECTION = 'anomaly_detection', // on all (nonlegacy) plans
	INSTANT_REFRESH = 'instant_refresh',
	SMART_ALERTS = 'smart_alerts', // del Sharing & Notifications
	AI_CHAT_ASSISTANT = 'ai_chat_assistant',
	AI_AGENTS = 'ai_agents',
	AI_ARTIFACTS = 'ai_artifacts',
	AI_MCP_CONNECTORS = 'ai_mcp_connectors',
	AI_ROUTINES = 'ai_routines',
	AI_SKILLS_CUSTOM = 'ai_skills_custom',
	AI_SKILLS_MARKETPLACE = 'ai_skills_marketplace',
	AI_SLACK_ASSISTANT = 'ai_slack_assistant',
	OKR = 'okr',
	BOARD_LOOPS = 'board_loops',
	CLIENTS = 'clients',
	MCP = 'mcp',
	SHARING = 'sharing',
	AI_CREDITS = 'ai_credits',
	SKIP_DW_BRANDING = 'skip_dw_branding', // allows user to disable datawall branding

	// Addons as features
	WHITE_LABELING = 'white_labeling',
	PRIORITY_SUPPORT = 'priority_support', // BillingAddonType.PRIORITY_SUPPORT.replace(/-/g, '_'),
	PRIORITY_SYNC = 'priority_sync',
	FISCAL_CALENDARS = 'fiscal_calendars',
	ACTIVITY_LOG = 'activity_log',
	ADVANCED_SECURITY = 'advanced_security',

	// secondary features, which are part of the main feature
	AI_DATABOARDS = 'ai_databoards',

	// advanced data sources - DATASETS
	ADVANCED_DATA_SOURCES = 'advanced_data_sources',
	ADVANCED_DATASET_FUNCTIONALITIES = 'advanced_dataset_functionalities',
	ADVANCED_DATA_SOURCES_LIMITED = 'advanced_data_sources_limited',

	// sql sources
	SQL_SOURCES = 'sql_sources',
	SQL_LEGACY_METRICS = 'sql_legacy_metrics',

	// gsheets
	GSHEETS_LEGACY_METRICS = 'gsheets_legacy_metrics',

	SHEET_SOURCES = 'sheet_sources',

	GOVERNANCE_VERIFICATION = 'governance_verification',
}

// Marketing-style feature comparison items shown in the plan comparison table.
// Not the same as BillingFeature (actual system capabilities) — one item may
// combine several BillingFeatures or plan attributes.
export const enum PlanFeatureItem {
	// Data & Integrations
	DATA_SOURCES = 'data_sources',
	CLOUD_INTEGRATIONS = 'cloud_integrations',
	CUSTOM_INTEGRATIONS = 'custom_integrations',
	DATASETS = 'datasets',
	DATABOX_API = 'databox_api',
	HISTORICAL_DATA = 'historical_data',
	MAX_SYNC_FREQUENCY = 'max_sync_frequency',

	AI_ANALYST = 'ai_analyst',
	AI_CREDITS = 'ai_credits',
	PERFORMANCE_SUMMARIES = 'performance_summaries',
	MCP_SERVER = 'mcp_server',
	ARTIFACTS = 'artifacts',
	PREBUILT_SKILLS = 'prebuilt_skills',
	CUSTOM_SKILLS = 'custom_skills',
	GENIE_IN_SLACK = 'genie_in_slack',
	ROUTINES = 'routines',
	AGENTS = 'agents',
	MCP_CONNECTORS = 'mcp_connectors',
	PREBUILT_METRICS_TEMPLATES = 'prebuilt_metrics_templates',
	ANOMALY_DETECTION = 'anomaly_detection',
	DASHBOARDS_REPORTS = 'dashboards_reports',
	CUSTOM_METRICS = 'custom_metrics',
	GOALS = 'goals',
	OKRS = 'okrs',
	ACCOUNT_TEMPLATES = 'account_templates',
	LOOPED_DATABOARDS = 'loops',
	REPORT_SLIDE_LIBRARY = 'report_slides',
	SSO = 'sso',
	SHARING = 'sharing',
	CSV_EXPORT = 'csv_export',
	USERS = 'users',
	FISCAL_CALENDAR = 'fiscal_calendar',
	WHITE_LABELING = 'white_labeling',
	SUB_ACCOUNTS = 'sub_accounts',
	CHAT_EMAIL_SUPPORT = 'chat_email_support',
	ACCOUNT_SETUP = 'account_setup',
	SECURITY_COMPLIANCE_REVIEW = 'security_compliance_review',
	GOVERNED_SEMANTIC_LAYER = 'governed_semantic_layer',
	DATA_LINEAGE = 'data_lineage',
	DATASET_TRANSFORMATIONS = 'dataset_transformations',
	VERIFY_BADGE_ASSETS = 'verify_badge_assets',
	CUSTOM_DATE_RANGES = 'custom_date_ranges',
	MEDIA_CREATIVES = 'media_creatives',
	DATA_STORIES = 'data_stories',
	UNBRANDED_DASHBOARDS = 'unbranded_dashboards',
	SMART_ALERTS = 'smart_alerts',
	DATA_SEMANTIC_LAYER_SETUP = 'data_semantic_layer_setup',
	AI_IMPLEMENTATION = 'ai_implementation',
	ENABLEMENT = 'enablement',
}

export const enum BillingPaymentMethod {
	CC = 'credit_card',
	INVOICE = 'send_invoice',
	BANK_ACC = 'bank_account',
}

export const enum BillingInvoiceStatus {
	OPEN = 'open',
	PAID = 'paid',
	DUE = 'due',
	DRAFT = 'draft',
	UNCOLLECTIBLE = 'uncollectible',
}

export const enum BillingServiceType {
	RESELLER_PARTNER = 'reseller-partner',
	CUSTOM_METRIC_DASHBOARD_CREATION = 'fake-id-FE-only',
}

export const enum BillingAddonType {
	WHITE_LABELING = 'white_labeling',
	PRIORITY_SUPPORT = 'priority-support',
	PRIORITY_SYNC = 'priority_sync',
	FISCAL_CALENDARS = 'fiscal_calendars',
	ACTIVITY_LOG = 'activity_log',
	ADVANCED_SECURITY = 'advanced_security',
	AI_CREDITS = 'ai_credits',
	CLIENT_PACK = 'client_pack',
	OKR = 'okr',
}

export const enum PaymentMethodBrand {
	AMEX = 'amex',
	AMERICAN_EXPRESS = 'American Express',
	DINERS = 'diners',
	DISCOVER = 'discover',
	DISCOVER_UPPERCASE = 'Discover',
	EFTPOS_AU = 'eftpos_au',
	JCB = 'jcb',
	LINK = 'link',
	MAESTRO = 'maestro',
	MASTER_CARD = 'mastercard',
	MASTER_CARD_UPPERCASE = 'MasterCard',
	UNION_PAY = 'unionpay',
	VISA = 'visa',
	VISA_UPPERCASE = 'Visa',
	UNKNOWN = 'unknown',
}

export const enum SlackMessage {
	DIRECT = '__DIRECT__',
}

export const enum MetricValueType {
	GENERAL = 'general',
	EVENT = 'event',
	CURRENT = 'current',
}

export const enum BlockFontSize {
	SMALL = 's',
	MEDIUM = 'm',
	LARGE = 'l',
}

export const enum Direction {
	UP = 'up',
	RIGHT = 'right',
	DOWN = 'down',
	LEFT = 'left',
}

export const enum AttachPosition {
	START = 'start',
	CENTER = 'center',
	END = 'end',
}

export { OrderDirection } from '@databox/types/domains/sort/sort.enums';

export const enum PaginationDirection {
	PREVIOUS = 'prev',
	NEXT = 'next',
}

export const enum CalendarType {
	GREGORIAN = 1,
	CUSTOM_FISCAL = 2,
	WEEK_ALIGNED_FISCAL = 3,
}

export const enum Theme {
	LIGHT = 'light',
	DARK = 'dark',
}

export const enum Size {
	TINY = 'tiny',
	SMALL = 'small',
	NORMAL = 'normal',
	BIG = 'big',
}

export const enum ReportSlideFragmentType {
	TITLE = 0,
	IMAGE = 1,
	BOARD = 2,
	BLOCK = 3,
	STORY_TEXT = 4,
	PLACEHOLDER = 99,
}

export const enum ReportFragmentAction {
	ADD = 'add',
	DELETE = 'delete',
	ORDER = 'order',
	RESIZE = 'resize',
	CONTENT = 'content',
	CHANGE_TYPE = 'change_type',
}

export const enum StoryElementType {
	METRIC = 'metric',
	DATE_RANGE = 'date_range',
	VALUE = 'value',
	GRANULATION_LABEL = 'granulation_label',
	COMPARE_TYPE = 'compare_type',
	COMPARE_CHANGE = 'compare_change',
	COMPARE_VALUE = 'compare_value',
	COMPARE_GRANULATION_LABEL = 'compare_granulation_label',
}

export const enum DataStoryType {
	POSITIVITY_GOOD = 1,
	POSITIVITY_OK = 2,
	POSITIVITY_BAD = 3,
	POSITIVITY_NEUTRAL = 4,
	FORECAST_CURRENT = 6, // scenario 1 - see Forecasts::Data::Story
	FORECAST_HISTORICAL_PAST = 7, // scenario 2
	FORECAST_HISTORICAL_FUTURE = 8, // scenario 3
}

export const enum SnapshotObjectType {
	BOARD = 1,
	LOOP = 2,
	REPORT = 3,
	SLIDE = 4,
}

export const enum ForecastMethod {
	DATABOX = 'databox_forecasting_methodology',
	LINEAR_REGRESSION = 'linear_regression',
	SIMPLE_EXPONENTIAL = 'simple_exponential_smoothing',
	LINEAR_CHANGE = 'linear_change',
	MOVING_AVERAGE_6M = 'six_month_moving_average',
	//TODO @marcel uncomment when supported on BE
	//AVERAGE = 'average',
	//MANUAL = 'manual',
}

export const enum ForecastStatusType {
	SNAPSHOT_GENERATING = 'snapshot_generating',
	PREVIEW_GENERATION_FAILED = 'preview_generation_failed',
	SNAPSHOT_GENERATION_FAILED = 'snapshot_generation_failed',
	COMPLETE = 'complete',
}

export const enum ForecastComparisonPeriod {
	NONE = 'none',
	PREVIOUS_PERIOD = 'previous_period',
	SAME_PERIOD_1Y_AGO = 'same_period_1y_ago',
}

export const enum InfolayerItemType {
	GENERAL = 'general',
	STATUS_OBJECTS = 'status-objects',
	REPORT_PDF = 'report-pdf',
	LOOP_PDF = 'loop-pdf',
	BOARD_SNAPSHOT = 'board-snapshot',
	VIEW_DIGEST = 'view-digest',
	VIEW_BOARD = 'view-board',
	DATA_EXPORT = 'data-export',
	DATASET_SYNC = 'dataset-sync',
	GENIE_CHAT_END = 'genie-chat-end',
}

export const enum ReminderObjectType {
	REPORT = 'reportReminder',
}

export const enum SheetColumnType {
	DATE = 'date', // must be same as SheetWizardStep.DATE
	METRIC = 'metric', // must be same as SheetWizardStep.METRIC
	DIMENSION = 'dimension', // must be same as SheetWizardStep.DIMENSION
}

export const enum SheetWizardStep {
	VERIFY = 'verify',
	METRIC = 'metric', // must be same as SheetColumnType.METRIC
	DIMENSION = 'dimension', // must be same as SheetColumnType.DIMENSION
	DATE = 'date', // must be same as SheetColumnType.DATE
	METRIC_DEFINITION = 'metric-definition',
}

export const enum ApiMetricBuilderWizardStep {
	REQUEST = 'request',
	METRIC = 'metric',
	DIMENSION = 'dimension',
	DATE = 'date',
	PREVIEW = 'preview',
	METRIC_DEFINITION = 'metric-definition',
}

export const enum TemplateObjectType {
	BOARD = 0,
	REPORT = 1,
	BOARD_LOOP = 2,
}

export const enum ChartLegendPosition {
	OFF = 0,
	BOTTOM = 1,
	TOP = 2,
	LEFT = 3,
	RIGHT = 4,
	OUTSIDE = 5,
}

export const enum MaxValueType {
	AUTO = 0,
	GOAL = 1,
	CUSTOM = 2,
	OTHER_METRIC = 3,
}

export const enum TableColumnType {
	SINGLE = 0,
	METRICS = 1,
	GRANULATIONS = 2,
	AUTO = 3,
}

export const enum DataLabelType {
	METRIC = 'metric',
	DIMENSION = 'dimension',
	OTHER = 'other',
}

export const enum YAxisScaleType {
	OFF = 0,
	AUTO = 1,
	CUSTOM = 2,
}

export const enum ShownLabels {
	NONE = '',
	VALUE = 'value',
	NAME = 'name',
}

export const enum ScorecardType {
	SCORECARD = 1,
	METRIC_DIGEST = 2,
}

export const enum DeliveryTimeType {
	FIRST_DAY_OF_THE_WEEK_8AM = 0,
	FRIDAY_4PM = 1,
	CUSTOM = 2,
}

export const enum DeliveryTimeDateRangeType {
	LAST_WEEK = 0,
	WEEK_TO_DATE = 1,
	THIS_WEEK = 2,
	LAST_7_DAYS = 3,
}

export const enum DataPerformanceSummaryType {
	GOAL = 'goal',
	BOARD = 'board',
	BOARD_PREVIEW = 'board-preview',
	BLOCK = 'block',
	DYNAMIC = 'dynamic',
}

export const enum DataPerformanceSummaryTrend {
	POSITIVE = 'positive',
	NEUTRAL = 'neutral',
	NEGATIVE = 'negative',
	UNAVAILABLE = 'unavailable',
}

export const enum DataPerformanceSummaryRequestBodyObjectType {
	BLOCK = 'block',
	BOARD = 'board',
	BOARD_PREVIEW = 'preview',
	DYNAMIC = 'dynamic',
}

export const enum DataPerformanceSummaryLimitResponseType {
	DAILY_ACCOUNT = 'daily_account',
	MONTHLY_ACCOUNT = 'monthly_account',
	DAILY_USER = 'daily_user',
}

export const enum EmblemType {
	NEW = 'new',
	ACCOUNT_TEMPLATE = 'account-template',
	PUBLIC_TEMPLATE = 'public-template',
	RECENTLY_DELETED = 'recently-deleted',
}

export const enum EventDeliveryTime {
	ANYTIME = 1,
	WEEKDAYS_WORKTIME = 11,
}

export const enum DataCategory {
	// values match tracking
	SAAS = 'SaaS',
	ECOMMERCE = 'eCommerce',
	MARKETING = 'Marketing',
	ADVERTISING = 'Paid Advertising',
	CRM_SALES = 'CRM & Sales',
	FINANCIALS = 'Financials',
	SOCIAL_MEDIA = 'Social Media',
	OTHER = 'Other',
}

export enum MetricLibrarySortBy {
	POPULARITY = 'Popularity',
	ALPHABETICALLY = 'Alphabetically',
}

export const enum ItemListView {
	GRID = 'grid',
	TABLE = 'table',
}

export const enum AccountNumberFormatType {
	AMERICAN = 0,
	EUROPEAN = 1,
}

export const enum BillingCancellationOptionType {
	// DISCOUNT = 'discount',
	// PAUSE = 'pause',
	SUPPORT = 'support',
	// DOWNGRADE = 'downgrade',
	CANCEL = 'cancel',
	ACCOUNT_REVIEW_ONLY = 'account_review_only',
}

export const enum BillingCancellationReason {
	AI_TOOLS = 610,
	OWN_AI_TOOL = 620,
	BI_COMPETITOR = 630,
	BUILT_IN_ANALYTICS = 640,
	UNRESOLVED_ISSUES = 650,
	STOPPED_REPORTING = 660,
}

export const enum AccountActivityState {
	ACTIVE = 1,
	// ACTIVE_NOTIFIED = 2,
	// INACTIVE = 3,
	// REACTIVATED_REFETCH = 4,
	// REACTIVATED_NOTIFIED = 5,
	PAUSED = 6,
}

export const enum ScriptDetailItemType {
	METRIC = 'metric',
	CONSTANT = 'constant',
}

export {
	BatchSelectActionType,
	BatchSelectObjectType,
} from '@databox/types/domains/batch/batch.enums';

export const enum PromotionIdentifier {
	TRIAL_GROWTH_FEATURES = 'trial_growth_features',
	GROWTH_PROMOTION = 'growth_promotion',
	TRIAL_HIGHER_PLAN = 'trial_higher_plan',
	TRIAL_HIGHER_PLAN_OFFER = 'trial_higher_plan_offer',
	TRIAL_ADDON = 'trial_addon',
}

export const enum PromotionType {
	TRIALING_FEATURES = 'trialing_features',
	DISCOUNT = 'discount',
}

// these are matching limitations from BE
export const enum DateRangeTypeId {
	// currents
	TODAY = 'Today',
	THIS_WEEK = 'ThisWeek',
	THIS_MONTH = 'ThisMonth',
	THIS_QUARTER = 'ThisQuarter',
	THIS_YEAR = 'ThisYear',
	TODAY_TO_HOUR = 'TodayToHour',
	WEEK_TO_DATE = 'WeekToDate',
	MONTH_TO_DATE = 'MonthToDate',
	QUARTER_TO_DATE = 'QuarterToDate',
	YEAR_TO_DATE = 'YearToDate',
	// previous
	YESTERDAY = 'Yesterday',
	PREVIOUS_WEEK = 'PreviousWeek',
	PREVIOUS_MONTH = 'PreviousMonth',
	PREVIOUS_QUARTER = 'PreviousQuarter',
	PREVIOUS_YEAR = 'PreviousYear',
	LAST_X_HOURS = 'LastXHours',
	LAST_X_HOURS_WOC = 'LastXHoursWithoutCurrent',
	LAST_X_DAYS = 'LastXDays',
	LAST_X_DAYS_WOC = 'LastXDaysWithoutCurrent',
	LAST_X_WEEKS = 'LastXWeeks',
	LAST_X_WEEKS_WOC = 'LastXWeeksWithoutCurrent',
	LAST_X_MONTHS = 'LastXMonths',
	LAST_X_MONTHS_WOC = 'LastXMonthsWithoutCurrent',
	LAST_X_QUARTERS = 'LastXQuarters',
	LAST_X_QUARTERS_WOC = 'LastXQuartersWithoutCurrent',
	LAST_X_YEARS = 'LastXYears',
	LAST_X_YEARS_WOC = 'LastXYearsWithoutCurrent',
	SINCE_TO_DATE = 'SinceToDate',
	SINCE_TO_DATE_WOC = 'SinceToDateWithoutCurrent',
	// future
	TOMORROW = 'Tomorrow',
	NEXT_WEEK = 'NextWeek',
	NEXT_MONTH = 'NextMonth',
	NEXT_QUARTER = 'NextQuarter',
	NEXT_YEAR = 'NextYear',
	// special
	ALL_TIME = 'AllTime',
	CUSTOM = 'CustomRange',
	LAST_X_DAYS_ROLLING = 'LastXDaysRolling',
}

// these are matching datepicker selection on UI (subset of limitations)
export const enum DateRangeSelectionId {
	// currents
	TODAY = DateRangeTypeId.TODAY, // TODAY & TODAY_TO_HOUR
	THIS_WEEK = DateRangeTypeId.THIS_WEEK,
	THIS_MONTH = DateRangeTypeId.THIS_MONTH,
	THIS_QUARTER = DateRangeTypeId.THIS_QUARTER,
	THIS_YEAR = DateRangeTypeId.THIS_YEAR,
	WEEK_TO_DATE = DateRangeTypeId.WEEK_TO_DATE,
	MONTH_TO_DATE = DateRangeTypeId.MONTH_TO_DATE,
	QUARTER_TO_DATE = DateRangeTypeId.QUARTER_TO_DATE,
	YEAR_TO_DATE = DateRangeTypeId.YEAR_TO_DATE,
	// previous
	YESTERDAY = DateRangeTypeId.YESTERDAY,
	PREVIOUS_WEEK = DateRangeTypeId.PREVIOUS_WEEK,
	PREVIOUS_MONTH = DateRangeTypeId.PREVIOUS_MONTH,
	PREVIOUS_QUARTER = DateRangeTypeId.PREVIOUS_QUARTER,
	PREVIOUS_YEAR = DateRangeTypeId.PREVIOUS_YEAR,
	LAST_X = 'LastX', // all LAST_X_* & LAST_X_*_WOC
	SINCE_TO_DATE = DateRangeTypeId.SINCE_TO_DATE, // SINCE_TO_DATE & SINCE_TO_DATE_WOC
	// future
	TOMORROW = DateRangeTypeId.TOMORROW,
	NEXT_WEEK = DateRangeTypeId.NEXT_WEEK,
	NEXT_MONTH = DateRangeTypeId.NEXT_MONTH,
	NEXT_QUARTER = DateRangeTypeId.NEXT_QUARTER,
	NEXT_YEAR = DateRangeTypeId.NEXT_YEAR,
	// special
	ALL_TIME = DateRangeTypeId.ALL_TIME,
	CUSTOM = DateRangeTypeId.CUSTOM, // CUSTOM & LAST_X_DAYS_ROLLING
}

// these are matching limitations from BE
export const enum GranulationTypeId {
	// commented are returned from BE but never used
	TOTAL = 'Total',
	HOURLY = 'Hourly',
	// HOURLY_2 = '2Hours',
	// HOURLY_6 = '6Hours',
	// HOURLY_12 = '12Hours',
	DAILY = 'Daily',
	WEEKLY = 'Weekly',
	MONTHLY = 'Monthly',
	QUARTERLY = 'Quarterly',
	YEARLY = 'Yearly',
	// NONE = 'None',
}

export enum KeyBenefitsTheme {
	DEFAULT = 'default',
	FREE = 'free',
	STARTER = 'starter',
	PROFESSIONAL = 'professional',
	GROWTH = 'growth',
	PREMIUM = 'premium',
}

// https://github.com/databox/core/blob/72dccf7731de7a8827ff9e85f96dfe70fe1e1d24/src/Z/Type/SourceStatus.php#L23
export const enum SourceStatusLevel {
	ACTIVE = 'active',
	WAIT = 'wait',
	WARNING = 'warning',
	ERROR = 'error',
}

export const enum AiSummaryContextLevel {
	BALANCED = 0,
	FACTUAL = 1,
	CONTEXTUAL = 2,
}

export const enum AiSummaryLength {
	NORMAL = 0,
	SHORT = 1,
	LONG = 2,
}

export const enum GoalType {
	GOAL = 0,
	OBJECTIVE = 1,
}

export const enum GoalCalculationStrategy {
	SUM = 'Sum',
	AVERAGE = 'Average',
	LAST_POINT = 'LastPoint',
}

export const enum GoalProgressType {
	AUTOMATIC = 0,
	MANUAL = 1,
	SUBGOALS = 2,
}

export const enum TimeframeType {
	Y = 0,
	Q1 = 1,
	Q2 = 2,
	Q3 = 3,
	Q4 = 4,
	H1 = 5,
	H2 = 6,
}

export const enum GoalTargetSuccessCondition {
	REACH = 1,
	METRIC = 2,
	ABOVE = 3,
	BELOW = 4,
}

// the delivery types a goal can use
export type GoalDeliveryType = DeliveryType.EMAIL | DeliveryType.SLACK;

export const enum GoalFormatType {
	NUMBER = 1,
	CURRENCY = 2,
	PERCENTAGE = 3,
}

export const enum GoalTargetValueType {
	FIXED = 1,
	PERCENTAGE = 2,
}

export const enum GoalOwnerScheduleType {
	WEEKLY = 1,
	BIWEEKLY = 2,
	MONTHLY = 3,
	QUARTERLY = 4,
}

export const enum GoalStatus {
	OTHER = 0,
	ON_TRACK = 1,
	AT_RISK = 2,
	OFF_TRACK = 3,
	ACHIEVED = 4,
	PARTIAL = 5,
	MISSED = 6,
}

// https://github.com/databox/core/blob/master/src/Z/Type/UserObjectSettingsObjectType.php
export const enum UserObjectSettingsObjectType {
	BOARD = 1,
	BLOCK = 2,
	GOAL = 3,
	BOARD_TEMPLATE = 4,
	BOARD_LOOP = 5,
	REPORT = 6,
	BOARD_LOOP_TEMPLATE = 7,
	REPORT_TEMPLATE = 8,
	TEAM = 9,
}

export enum AnnotationType {
	DEFAULT = 0,
	ANOMALY = 1,
}

export enum TeamType {
	OPEN = 1,
	CLOSED = 2,
	PRIVATE = 3,
}

export const enum TeamSectionItemType {
	METRIC = 1,
	GOAL = 2,
	DATABOARD = 3,
	REPORT = 4,
	DATABOARD_LOOP = 5,
}

export enum TeamMemberStatus {
	MEMBER = 1,
	REQUEST = 2,
}

export enum TrackingRawDataFeature {
	DATABOARD = 'databoards',
	REPORT = 'report',
	METRIC_INSIGHTS = 'metric_insights',
}

export enum ReportIssueSenderType {
	ANALYTICS_API = 'analytics-api',
	DATASET_EVENTS = 'dataset-events',
}

export enum ReportIssueProjectId {
	ANALYTICS_API = '4505029392662528',
	DATASET_EVENTS = '4508007443791872',
}

export {
	FilterItemType,
	FilterLogicalOperator,
	MetricFilterOperator,
} from '@databox/types/domains/metric-setup/metric-setup.enums';

export const enum MetricDetailsSettingsTab {
	VISUALIZE = 'Visualize',
	METHODOLOGY = 'Methodology',
	ANNOTATE = 'Annotate',
	ABOUT = 'Details',
}

export const enum MetricDetailsTab {
	EXPLORE = 'explore',
	FORECAST = 'forecast',
}

// from app/components/common/popup-context-switch.gts
export const enum PopupContextSwitchAction {
	DUPLICATE,
	ADD_TO,
}

// from app/components/common/popup-context-switch.gts
export const enum PopupContextSwitchModelType {
	BOARD = 'dashboard',
	BLOCK = 'datablock',
	LOOP = 'looped dashboard',
	REPORT = 'report',
	SLIDE = 'report slide',
}

// from app/components/reports/cover-pageless.gts
export enum ReportCoverAction {
	ADD = 'addCover',
	EDIT = 'editCover',
}

// from app/components/reports/popup-share.gts
export const enum ReportPopupShareTab {
	LINK = 'shareable-link',
	DOWNLOAD = 'download',
	SEND = 'send',
	EMBED = 'embed',
}

export enum AlertEventCondition {
	// from BE: constantsService.eventConditions
	IS_LESS_THAN = 1,
	IS_GREATER_THAN = 2,
	DECREASED_BY_MORE_THAN = 3,
	INCREASED_BY_MORE_THAN = 4,
	PERCENT_DECREASED_BY_MORE_THAN = 5,
	PERCENT_INCREASED_BY_MORE_THAN = 6,
	INCREASED_OR_DECREASED_BY_MORE_THAN = 7,
	PERCENT_INCREASED_OR_DECREASED_BY_MORE_THAN = 14,
}

export { VerificationStatus } from '@databox/types/domains/verification/verification.enums';

export enum VerificationObjectType {
	BOARD = 701,
	REPORT = 705,
	CUSTOM_QUERY = 707,
	CALCULATED_METRIC = 708,
	BASIC_METRIC = 715,
	DATASET = 721,
}
